/* eslint-disable no-unused-vars */
/* eslint-disable no-constant-condition */
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import * as Cookies from 'js-cookie'
import { withRouter } from 'next/router'
import { useDispatch } from 'react-redux'

import API from 'api'
import { convertToQueryString } from 'helpers'
import { isCapacitor } from 'helpers/isCapacitor'
import { SignInWithApple } from 'helpers/capacitorAppleSignInWrapper'
import { getAppleLoginKey } from 'models/application'
import { attemptLogin } from 'redux-web/utils/authentication/actions'

import AppleSignInButton from './Screen'

const AppleSignInButtonHOC = WrapperComponent => {
  const Wrapper = props => {
    const { router, callbackRedirection, application, skipLoginFlow } = props
    const dispatch = useDispatch()

    const appleKey = useMemo(() => getAppleLoginKey(application), [application])
    const redirectURI = `${process.env.NEXT_PUBLIC_API_URL}login/apple/${appleKey}`
    const redirectCallback = () => {
      Cookies.set(
        'citifyd_apple_login_redirect',
        callbackRedirection || router.asPath
      )

      if (skipLoginFlow) {
        Cookies.set('pay_by_signage_flow', true)
      } else {
        Cookies.remove('pay_by_signage_flow')
      }
    }

    const data = useMemo(
      () => ({
        client_id: 'com.citifyd.web',
        // nonce: 'nonce',
        redirect_uri: `${process.env.NEXT_PUBLIC_API_URL}login/apple/${appleKey}`,
        response_mode: 'form_post',
        response_type: 'code',
        scope: 'name email',
        usePopup: true,
      }),
      [appleKey]
    )

    const querystring = useMemo(() => convertToQueryString(data), [data])
    const appleLink = useMemo(
      () => `${process.env.NEXT_PUBLIC_APPLE_LOGIN_API_URL}?${querystring}`,
      [querystring]
    )

    const options = {
      clientId: 'com.citifyd.web',
      redirectURI: `${process.env.NEXT_PUBLIC_API_URL}login/apple/${appleKey}`,
      scopes: 'name email',
      state: '12345',
      nonce: 'nonce',
    }

    const handleCapacitorAppleLogin = async () => {
      try {
        const appleResponse = await SignInWithApple.authorize(options)

        const payload = {
          code: appleResponse?.response?.authorizationCode,
        }

        if (appleResponse?.response?.givenName) {
          payload.user = {
            name: {
              firstName: appleResponse.response.givenName || '',
              lastName: appleResponse.response.familyName || '',
            },
          }
        }

        if (appleResponse?.response?.email) {
          if (!payload.user) {
            payload.user = {}
          }
          payload.user.email = appleResponse.response.email || ''
        }

        const responseApi = await API().appleSignIn({
          headers: {
            'app-key': 'd7022082-5af0-4d93-812d-2403a27232fc',
          },
          body: JSON.stringify(payload),
        })

        switch (responseApi.outcome) {
          case 'USER_EXISTS': {
            if (!responseApi.data?.authenticationToken) {
              throw new Error('Something went wrong')
            }

            dispatch(
              attemptLogin({
                data: {
                  authenticationToken: responseApi.data.authenticationToken,
                },
              })
            )

            break
          }
          case 'NEW_USER': {
            const getNameAvailable = () => {
              const firstName = payload.user?.name?.firstName
              const lastName = payload.user?.name?.lastName

              if (firstName && !lastName) {
                return `${firstName}`
              }

              if (firstName && lastName) {
                return `${firstName}+${lastName}`
              }

              return ''
            }

            const nameAvailable = getNameAvailable()
            const appleLoginToken = responseApi.data?.appleLoginToken
            const signupPath = `/signup?apple_login_token=${appleLoginToken}&name_available=${nameAvailable}&email_available=${payload.user?.email}`

            await router.push(signupPath)

            break
          }
          default: {
            throw new Error('Something went wrong')
          }
        }
      } catch (error) {
        console.error(error)
      }
    }

    if (isCapacitor) {
      return (
        <WrapperComponent
          appleLink={appleLink}
          onClick={handleCapacitorAppleLogin}
          {...props}
        />
      )
    }

    return (
      <WrapperComponent
        appleLink={appleLink}
        redirectCallback={redirectCallback}
        {...props}
      />
    )
  }

  Wrapper.propTypes = {
    callbackRedirection: PropTypes.string,
    router: PropTypes.object,
    application: PropTypes.string,
    skipLoginFlow: PropTypes.bool,
  }

  return Wrapper
}

export default withRouter(AppleSignInButtonHOC(AppleSignInButton))
